// Admin Payout Detail — the screen where transitions actually happen.
//
// Status-driven action panel:
//   requested  → Approve & Mark Processing OR Reject
//   processing → Mark Completed OR Mark Failed
//   terminal   → read-only summary + audit log
//
// Audit-log inserts and notifications happen server-side in /admin/payouts/:id/*
// route handlers, so the client just hits the right endpoint and refreshes.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface PayoutDetail {
  id: string;
  user_id: string;
  wallet_id: string;
  amount: number;
  currency: string;
  payout_rail: string;
  status: string;
  external_reference: string | null;
  failure_reason: string | null;
  processor_notes: string | null;
  rail_details: {
    rail?: string;
    account_holder_name?: string;
    details?: Record<string, unknown>;
    payout_account_id?: string;
  } | null;
  requested_at: string;
  approved_at: string | null;
  processed_at: string | null;
  completed_at: string | null;
  processed_by: string | null;
  profiles: {
    id: string;
    username: string;
    avatar_url: string | null;
    country_code: string | null;
    platform_status: string;
    platform_ban_reason: string | null;
    created_at: string;
  } | null;
}

interface AuditEntry {
  id: string;
  old_status: string | null;
  new_status: string;
  reason: string | null;
  created_at: string;
  profiles: { id: string; username: string } | null;
}

interface AccountInfo {
  id: string;
  rail: string;
  account_holder_name: string;
  details: Record<string, unknown>;
  country: string | null;
  currency: string | null;
  verified: boolean;
  created_at: string;
  last_used_at: string | null;
}

interface DetailResponse {
  payout: PayoutDetail;
  wallet: {
    balance: number;
    lifetime_earned: number;
    lifetime_paid: number;
  } | null;
  account: AccountInfo | null;
  payout_history_count: number;
  audit_log: AuditEntry[];
}

const RAIL_LABELS: Record<string, string> = {
  bank: "Bank transfer",
  mobile_money: "Mobile money",
  other: "Other",
};

function formatUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function statusColor(status: string): string {
  switch (status) {
    case "completed":
      return "var(--upvote, #22C55E)";
    case "failed":
    case "cancelled":
      return "var(--danger, #ef4444)";
    case "processing":
      return "var(--accent)";
    default:
      return "var(--text-secondary)";
  }
}

function copyToClipboard(text: string): void {
  if (navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(text);
  }
}

export default function AdminPayoutDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action dialogs
  const [actionModal, setActionModal] = useState<
    null | "approve" | "complete" | "fail" | "reject"
  >(null);
  const [actionReason, setActionReason] = useState("");
  const [actionReference, setActionReference] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Editable notes (non-action — saved separately)
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api<DetailResponse>(`/admin/payouts/${id}`);
      setData(res);
      setNotesDraft(res.payout.processor_notes ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load payout");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  function openModal(action: "approve" | "complete" | "fail" | "reject") {
    setActionModal(action);
    setActionReason("");
    setActionReference("");
    setActionNotes(data?.payout.processor_notes ?? "");
    setActionError(null);
  }

  async function submitAction() {
    if (!id || !actionModal) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const body: Record<string, unknown> = {};
      if (actionModal === "complete") {
        body.external_reference = actionReference.trim();
      }
      if (actionModal === "fail" || actionModal === "reject") {
        body.failure_reason = actionReason.trim();
      }
      if (actionNotes.trim()) {
        body.processor_notes = actionNotes.trim();
      }
      await api(`/admin/payouts/${id}/${actionModal}`, {
        method: "POST",
        body,
      });
      setActionModal(null);
      await fetchDetail();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Action failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function saveNotes() {
    if (!id) return;
    setNotesSaving(true);
    try {
      await api(`/admin/payouts/${id}/notes`, {
        method: "PATCH",
        body: { processor_notes: notesDraft },
      });
      await fetchDetail();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to save notes");
    } finally {
      setNotesSaving(false);
    }
  }

  async function flagAccount() {
    if (!data?.account) return;
    if (
      !confirm(
        "Flag this account as suspicious? It will be marked unverified and the creator will need to re-confirm before another withdrawal.",
      )
    )
      return;
    try {
      await api(`/admin/payouts/accounts/${data.account.id}/flag`, {
        method: "POST",
      });
      await fetchDetail();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to flag account");
    }
  }

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <Spinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <div style={styles.errorBar}>{error ?? "Payout not found"}</div>
        <Button
          variant="secondary"
          onClick={() => navigate("/admin/payouts")}
          style={{ marginTop: 12 }}
        >
          Back to queue
        </Button>
      </Card>
    );
  }

  const { payout, wallet, account, payout_history_count, audit_log } = data;
  const creator = payout.profiles;

  // Warnings
  const warnings: string[] = [];
  if (creator?.platform_status === "banned") {
    warnings.push(`Creator is platform-banned: ${creator.platform_ban_reason ?? "no reason on file"}`);
  }
  if (creator?.platform_status === "under_review") {
    warnings.push("Creator is under platform review.");
  }
  if (
    account?.created_at &&
    Date.now() - new Date(account.created_at).getTime() < 24 * 3600 * 1000
  ) {
    warnings.push("Payout account was created less than 24 hours ago.");
  }
  if (wallet && Number(wallet.balance) < 0) {
    warnings.push(
      `Creator's wallet balance is negative (${formatUsd(wallet.balance)}). This payout should not be processed until reconciled.`,
    );
  }

  const isQueueState = payout.status === "requested";
  const isProcessing = payout.status === "processing";
  const isTerminal =
    payout.status === "completed" ||
    payout.status === "failed" ||
    payout.status === "cancelled";

  // Plain-text bundle for one-click copy into the operator's banking app.
  function detailsCopyBlob(): string {
    const lines: string[] = [
      `Amount: ${formatUsd(payout.amount)} ${payout.currency}`,
      `Rail: ${RAIL_LABELS[payout.payout_rail] ?? payout.payout_rail}`,
      `Account holder: ${payout.rail_details?.account_holder_name ?? ""}`,
    ];
    const d = payout.rail_details?.details ?? {};
    for (const [k, v] of Object.entries(d)) {
      lines.push(`${k.replace(/_/g, " ")}: ${String(v)}`);
    }
    if (account?.country) lines.push(`Country: ${account.country}`);
    if (account?.currency)
      lines.push(`Receiving currency: ${account.currency.toUpperCase()}`);
    return lines.join("\n");
  }

  return (
    <div>
      <div style={styles.header}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/admin/payouts")}
        >
          ← Queue
        </Button>
        <div style={styles.headerAmount}>
          {formatUsd(payout.amount)}{" "}
          <span style={{ color: statusColor(payout.status), fontSize: 16 }}>
            · {payout.status}
          </span>
        </div>
      </div>

      {warnings.length > 0 && (
        <Card style={{ marginBottom: 16, borderColor: "var(--danger, #ef4444)" }}>
          <CardTitle style={{ color: "var(--danger, #ef4444)" }}>
            Warnings
          </CardTitle>
          <ul style={styles.warningList}>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Card>
      )}

      <div style={styles.twoCol}>
        {/* Left column: creator + details */}
        <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <CardTitle>Creator</CardTitle>
            <div style={styles.creatorBlock}>
              {creator?.avatar_url ? (
                <img
                  src={creator.avatar_url}
                  alt=""
                  style={styles.avatarLg}
                />
              ) : (
                <div style={styles.avatarLgPlaceholder} />
              )}
              <div>
                <div style={styles.creatorName}>
                  @{creator?.username ?? "(deleted)"}
                </div>
                <CardSubtle>
                  {creator?.country_code ?? "—"} · Account opened{" "}
                  {creator?.created_at
                    ? new Date(creator.created_at).toLocaleDateString()
                    : "?"}
                </CardSubtle>
                <CardSubtle>
                  Lifetime earned:{" "}
                  {wallet ? formatUsd(wallet.lifetime_earned) : "—"} · Paid:{" "}
                  {wallet ? formatUsd(wallet.lifetime_paid) : "—"} · Wallet:{" "}
                  {wallet ? formatUsd(wallet.balance) : "—"}
                </CardSubtle>
                <CardSubtle>
                  {payout_history_count} prior payout
                  {payout_history_count === 1 ? "" : "s"}
                </CardSubtle>
              </div>
            </div>
          </Card>

          <Card>
            <div style={styles.cardHeaderRow}>
              <CardTitle>Payout details</CardTitle>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => copyToClipboard(detailsCopyBlob())}
              >
                Copy all
              </Button>
            </div>
            <div style={styles.detailGrid}>
              <DetailRow
                k="Method"
                v={RAIL_LABELS[payout.payout_rail] ?? payout.payout_rail}
              />
              <DetailRow
                k="Account holder"
                v={payout.rail_details?.account_holder_name ?? "—"}
                copy={payout.rail_details?.account_holder_name}
              />
              {Object.entries(payout.rail_details?.details ?? {}).map(
                ([k, v]) => (
                  <DetailRow
                    key={k}
                    k={k.replace(/_/g, " ")}
                    v={String(v)}
                    copy={String(v)}
                  />
                ),
              )}
              {account?.country && (
                <DetailRow k="Country" v={account.country} />
              )}
              {account?.currency && (
                <DetailRow
                  k="Receiving currency"
                  v={account.currency.toUpperCase()}
                />
              )}
            </div>
            {account && (
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                {!account.verified && (
                  <CardSubtle style={{ color: "var(--danger, #ef4444)" }}>
                    Account flagged as unverified
                  </CardSubtle>
                )}
                {account.verified && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={flagAccount}
                  >
                    Flag account suspicious
                  </Button>
                )}
              </div>
            )}
          </Card>

          <Card>
            <CardTitle>Operator notes</CardTitle>
            <CardSubtle style={{ marginBottom: 8 }}>
              Visible to other admins in the audit log. Not shown to the
              creator.
            </CardSubtle>
            <Textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={4}
              disabled={isTerminal}
            />
            {!isTerminal && (
              <div style={{ marginTop: 8 }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={saveNotes}
                  disabled={
                    notesSaving ||
                    notesDraft === (payout.processor_notes ?? "")
                  }
                >
                  {notesSaving ? "Saving…" : "Save notes"}
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Right column: actions + timeline */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <CardTitle>Actions</CardTitle>
            {isQueueState && (
              <div style={styles.actionStack}>
                <Button variant="primary" onClick={() => openModal("approve")}>
                  Approve & mark processing
                </Button>
                <Button variant="danger" onClick={() => openModal("reject")}>
                  Reject
                </Button>
              </div>
            )}
            {isProcessing && (
              <div style={styles.actionStack}>
                <Button variant="primary" onClick={() => openModal("complete")}>
                  Mark completed
                </Button>
                <Button variant="danger" onClick={() => openModal("fail")}>
                  Mark failed
                </Button>
              </div>
            )}
            {isTerminal && (
              <CardSubtle>
                This payout is in a terminal state. No further actions.
              </CardSubtle>
            )}
            {payout.external_reference && (
              <div style={{ marginTop: 12 }}>
                <CardSubtle>External reference</CardSubtle>
                <div style={styles.refValue}>
                  {payout.external_reference}
                </div>
              </div>
            )}
            {payout.failure_reason && (
              <div style={{ marginTop: 12 }}>
                <CardSubtle>
                  {payout.status === "cancelled"
                    ? "Rejection reason"
                    : "Failure reason"}
                </CardSubtle>
                <div style={styles.refValue}>{payout.failure_reason}</div>
              </div>
            )}
          </Card>

          <Card>
            <CardTitle>Timeline</CardTitle>
            <div style={styles.timeline}>
              <TimelineRow
                label="Requested"
                at={payout.requested_at}
                by={creator?.username ?? "(creator)"}
              />
              {payout.approved_at && (
                <TimelineRow
                  label="Approved"
                  at={payout.approved_at}
                  by={"admin"}
                />
              )}
              {payout.processed_at && (
                <TimelineRow
                  label="Processing"
                  at={payout.processed_at}
                  by={"admin"}
                />
              )}
              {payout.completed_at && (
                <TimelineRow
                  label="Completed"
                  at={payout.completed_at}
                  by={"admin"}
                />
              )}
            </div>
          </Card>

          <Card>
            <CardTitle>Audit log</CardTitle>
            {audit_log.length === 0 ? (
              <CardSubtle>No log entries yet.</CardSubtle>
            ) : (
              <div style={styles.timeline}>
                {audit_log.map((entry) => (
                  <div key={entry.id} style={styles.auditRow}>
                    <div style={styles.auditPrimary}>
                      {entry.old_status ?? "—"} → {entry.new_status}
                    </div>
                    <CardSubtle>
                      {entry.profiles?.username
                        ? `@${entry.profiles.username}`
                        : "system"}{" "}
                      · {new Date(entry.created_at).toLocaleString()}
                    </CardSubtle>
                    {entry.reason && (
                      <CardSubtle style={{ fontStyle: "italic" }}>
                        {entry.reason}
                      </CardSubtle>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Action modal */}
      <Modal
        open={actionModal !== null}
        onClose={() => !submitting && setActionModal(null)}
        title={
          actionModal === "approve"
            ? "Approve and mark processing"
            : actionModal === "complete"
              ? "Mark payout completed"
              : actionModal === "fail"
                ? "Mark payout failed"
                : actionModal === "reject"
                  ? "Reject payout request"
                  : ""
        }
      >
        {actionModal === "approve" && (
          <CardSubtle style={{ marginBottom: 12 }}>
            Use this after you've initiated the transfer in your banking /
            mobile money app. You'll come back here once the transfer is
            confirmed to mark it completed.
          </CardSubtle>
        )}
        {actionModal === "complete" && (
          <>
            <Label>External reference *</Label>
            <Input
              value={actionReference}
              onChange={(e) => setActionReference(e.target.value)}
              placeholder="Remitly confirmation, bank wire ID, M-Pesa code, etc."
            />
            <CardSubtle style={{ marginTop: 6 }}>
              This gets sent to the creator and shows up in their payout
              history. Paste exactly as the provider showed it.
            </CardSubtle>
          </>
        )}
        {(actionModal === "fail" || actionModal === "reject") && (
          <>
            <Label>
              {actionModal === "reject" ? "Rejection" : "Failure"} reason *
            </Label>
            <Textarea
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              rows={3}
              placeholder={
                actionModal === "reject"
                  ? "e.g. Account details look incorrect; please re-enter"
                  : "e.g. Bank rejected — invalid routing number"
              }
            />
            <CardSubtle style={{ marginTop: 8 }}>
              The creator will receive a notification with this reason.
              Funds are returned to their wallet automatically.
            </CardSubtle>
          </>
        )}

        <Label style={{ marginTop: 12 }}>Operator notes (optional)</Label>
        <Textarea
          value={actionNotes}
          onChange={(e) => setActionNotes(e.target.value)}
          rows={2}
        />

        {actionError && (
          <div style={{ ...styles.errorBar, marginTop: 12 }}>{actionError}</div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => setActionModal(null)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant={
              actionModal === "fail" || actionModal === "reject"
                ? "danger"
                : "primary"
            }
            onClick={submitAction}
            disabled={
              submitting ||
              (actionModal === "complete" && !actionReference.trim()) ||
              ((actionModal === "fail" || actionModal === "reject") &&
                !actionReason.trim())
            }
          >
            {submitting ? "Submitting…" : "Confirm"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function DetailRow({
  k,
  v,
  copy,
}: {
  k: string;
  v: string;
  copy?: string;
}) {
  return (
    <div style={styles.detailRow}>
      <div style={styles.detailKey}>{k}</div>
      <div style={styles.detailValRow}>
        <span style={styles.detailVal}>{v}</span>
        {copy && (
          <button
            onClick={() => copyToClipboard(copy)}
            style={styles.copyBtn}
            aria-label={`Copy ${k}`}
            title="Copy"
          >
            ⧉
          </button>
        )}
      </div>
    </div>
  );
}

function TimelineRow({
  label,
  at,
  by,
}: {
  label: string;
  at: string;
  by: string;
}) {
  return (
    <div style={styles.auditRow}>
      <div style={styles.auditPrimary}>{label}</div>
      <CardSubtle>
        {by} · {formatDateTime(at)}
      </CardSubtle>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  headerAmount: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--text-primary)",
  },
  twoCol: { display: "flex", gap: 16, alignItems: "flex-start" },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  creatorBlock: { display: "flex", gap: 12, alignItems: "flex-start" },
  avatarLg: { width: 48, height: 48, borderRadius: 24, objectFit: "cover" },
  avatarLgPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    background: "var(--border)",
  },
  creatorName: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--text-primary)",
    marginBottom: 2,
  },
  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  detailGrid: { display: "flex", flexDirection: "column", gap: 8 },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: "1px solid var(--border)",
  },
  detailKey: {
    fontSize: 13,
    color: "var(--text-secondary)",
    textTransform: "capitalize",
  },
  detailValRow: { display: "flex", alignItems: "center", gap: 8 },
  detailVal: {
    fontSize: 14,
    color: "var(--text-primary)",
    fontFamily: "ui-monospace, monospace",
  },
  copyBtn: {
    background: "transparent",
    border: "none",
    color: "var(--text-muted)",
    cursor: "pointer",
    fontSize: 14,
    padding: "2px 6px",
  },
  actionStack: { display: "flex", flexDirection: "column", gap: 8 },
  refValue: {
    fontFamily: "ui-monospace, monospace",
    fontSize: 13,
    color: "var(--text-primary)",
    background: "rgba(255,255,255,0.04)",
    padding: 8,
    borderRadius: "var(--radius-sm)",
    marginTop: 4,
  },
  timeline: { display: "flex", flexDirection: "column", gap: 12 },
  auditRow: {
    paddingLeft: 8,
    borderLeft: "2px solid var(--border)",
  },
  auditPrimary: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-primary)",
    marginBottom: 2,
  },
  warningList: {
    margin: 0,
    paddingLeft: 18,
    color: "var(--text-primary)",
  },
};
